import { MIN_TRIM_SECONDS, SHOTSTACK_FPS } from './types.ts';

/** Round a time in seconds to the nearest Shotstack frame. */
export function roundToFrame(seconds: number, fps = SHOTSTACK_FPS): number {
  if (!Number.isFinite(seconds)) return 0;
  const frames = Math.round(seconds * fps);
  return Math.max(0, frames / fps);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export interface TrimRange {
  start: number;
  end: number;
}

export interface ValidatedTrim extends TrimRange {
  duration: number;
  sourceDuration: number;
  adjusted: boolean;
  message?: string;
}

export function validateTrim(
  start: number,
  end: number,
  sourceDuration: number,
): { ok: true; trim: ValidatedTrim } | { ok: false; error: string } {
  const source = roundToFrame(Math.max(0, sourceDuration));
  if (source < MIN_TRIM_SECONDS) {
    return { ok: false, error: 'Source clip is too short to trim' };
  }

  if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) {
    return { ok: false, error: 'Start must be before end' };
  }
  if (start < -1 / SHOTSTACK_FPS || end > source + 1 / SHOTSTACK_FPS) {
    return { ok: false, error: 'Trim points must stay within the source duration' };
  }

  const rawStart = roundToFrame(start);
  const rawEnd = roundToFrame(end);

  if (rawStart >= rawEnd) {
    return { ok: false, error: 'Start must be before end' };
  }

  const clampedStart = clamp(rawStart, 0, Math.max(0, source - MIN_TRIM_SECONDS));
  const clampedEnd = clamp(rawEnd, clampedStart + MIN_TRIM_SECONDS, source);
  if (clampedEnd - clampedStart < MIN_TRIM_SECONDS) {
    return { ok: false, error: `Selected duration must be at least ${MIN_TRIM_SECONDS}s` };
  }

  return {
    ok: true,
    trim: {
      start: clampedStart,
      end: clampedEnd,
      duration: roundToFrame(clampedEnd - clampedStart),
      sourceDuration: source,
      adjusted: clampedStart !== rawStart || clampedEnd !== rawEnd,
    },
  };
}

/** Reset or clamp a trim after a replacement clip changes duration. */
export function fitTrimToDuration(
  trimStart: number,
  trimEnd: number,
  nextDuration: number,
): ValidatedTrim {
  const source = roundToFrame(Math.max(MIN_TRIM_SECONDS, nextDuration));
  const selected = roundToFrame(Math.max(0, trimEnd - trimStart));
  const originalEndExceeds = trimEnd > source + 1 / SHOTSTACK_FPS;
  const originalStartExceeds = trimStart >= source - 1 / SHOTSTACK_FPS;

  if (originalEndExceeds || originalStartExceeds || selected > source) {
    return {
      start: 0,
      end: source,
      duration: source,
      sourceDuration: source,
      adjusted: true,
      message: 'Trim was reset because the replacement clip has a different duration.',
    };
  }

  const start = clamp(roundToFrame(trimStart), 0, Math.max(0, source - MIN_TRIM_SECONDS));
  const end = clamp(roundToFrame(start + selected), start + MIN_TRIM_SECONDS, source);

  if (end - start < MIN_TRIM_SECONDS || start >= source) {
    return {
      start: 0,
      end: source,
      duration: source,
      sourceDuration: source,
      adjusted: true,
      message: 'Trim was reset because the replacement clip has a different duration.',
    };
  }

  const adjusted = start !== roundToFrame(trimStart) || end !== roundToFrame(trimEnd) || source !== roundToFrame(trimEnd);
  return {
    start,
    end,
    duration: roundToFrame(end - start),
    sourceDuration: source,
    adjusted: adjusted && (start !== 0 || end !== source || selected !== source),
    message: adjusted
      ? 'Trim was adjusted to fit the replacement clip duration.'
      : undefined,
  };
}

export function formatTimecode(seconds: number): string {
  const safe = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const mins = Math.floor(safe / 60);
  const secs = safe - mins * 60;
  const whole = Math.floor(secs);
  const frac = Math.round((secs - whole) * 10);
  if (frac === 10) {
    return `${mins}:${String(whole + 1).padStart(2, '0')}.0`;
  }
  return `${mins}:${String(whole).padStart(2, '0')}.${frac}`;
}

export function parseTimeInput(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d+:\d+(?:\.\d+)?$/.test(trimmed)) {
    const [m, s] = trimmed.split(':');
    const seconds = Number(m) * 60 + Number(s);
    return Number.isFinite(seconds) ? seconds : null;
  }
  const asNumber = Number(trimmed);
  return Number.isFinite(asNumber) ? asNumber : null;
}
